import { BaseViewStore } from 'core/stores';
import { action, observable, makeObservable } from 'mobx';
import { roles, sourceOfChange } from 'common/constants';
import { FulfillmentOrderStore, FulfillmentOrderProcessingStore } from 'modules/order/stores';
import { PackingSlipStore } from 'modules/packing-slip/stores';
import { ShipmentInfoStore } from 'modules/shipment-info/stores';
import {
	onPrintLabelStatuses,
	onPrintPackingSlipStatuses,
	fulfillmentOrderStatus,
	channelType,
} from 'common/constants';
import { notificationError, isSeller, isSome, handleVoidLabelResponseNotification } from 'core/utils';
import { ConfirmModalParams, ModalParams } from 'core/models';
import { createFileUrl, isSuperAdminOrganization } from 'core/utils';
import moment from 'moment';
import _ from 'lodash';

class OrderDetailViewStore extends BaseViewStore {
	@observable canCancel = false;
	@observable fulfillmentOrder = null;
	@observable activeShipmentInfo = null;
	@observable actionMenuOpen = false;
	@observable shouldDisplayActionButton = false;
	@observable hasLabel = false;
	@observable productionModeCheck = false;
	@observable selectedItem;
	@observable editFulfillmentOrderSplit = false;
	reload = false;
	openPrintModal = false;
	pdfURL = null;
	isPrintingPackingSlipAllowed = null;
	fulfillmentOrderItems = null;
	plainLabelVoiding = true;
	reoptimizeLabelVoiding = false;
	itemPrice = 0;
	order = null;
	hasActiveShipment = false;
	fulfillmentOrderStatus;
	packingSlipStore = null;
	isBuyShipping = false;
	showVoidOnChannel = false;
	isMultiItem = false;
	isReentered = false;
	orderAddressData = null;

	constructor(rootStore) {
		super(rootStore);
		makeObservable(this);
		this.fulfillmentOrderStore = new FulfillmentOrderStore(rootStore);
		this.shipmentInfoStore = new ShipmentInfoStore(rootStore);
		this.packingSlipStore = new PackingSlipStore(rootStore);
		this.rootStore.viewStore.modes = [];
		this.isSuperAdminOrganization = isSuperAdminOrganization(rootStore);
		this.isSeller = isSeller(rootStore);
		this.hasPiiAccess = this.rootStore.authStore.hasPiiAccess;
		this.isWarehouseAdministrator =
			_.findIndex(
				rootStore.app.baasic.userHandler.user.user.roles,
				role => role === roles.warehouseAccountOwner || role === roles.warehouseAdministrator
			) != -1;
		this.initialize();
	}

	@action.bound
	initialize = async () => {
		this.suspend();
		await this.getFulfillmentOrder(this.rootStore.routerStore.routerState.params.id, {
			embed: [
				'shippingCoreCountry',
				'shippingCoreCountryState',
				'carrier',
				'carrierService',
				'cancelReason',
				'originalFulfillmentOrder',
				'seller',
				'seller.sellerOrganizationInfo',
				'warehouseOrganization',
				'warehouseOrganization.convertWarehouses',
				'warehouseOrganization.warehouseOrganizationInfo',
				'warehouseOrganization.warehouseOrganizationInfo.address',
				'warehouseOrganization.warehouseOrganizationInfo.contact',
				'warehouseOrganization.warehouseOrganizationInfo.address.coreCountry',
				'warehouseOrganization.warehouseOrganizationInfo.address.coreCountryState',
				'onHoldReason',
				'replacementReason',
				'fulfillmentOrderItems',
				'fulfillmentOrderItems.orderLine',
				'fulfillmentOrderItems.orderLine.product',
				'fulfillmentOrderItems.product',
				'shipmentInfos',
				'fulfillmentOrderStatus',
				'fulfillmentOrderErrors',
				'shipmentInfos.shipmentInfoStatus',
				'order',
				'order.orderLines',
				'order.shippingCoreCountry',
				'order.shippingCoreCountryState',
				'order.confirmationType',
				'order.carrier',
				'order.channel',
				'order.channel.channelType',
				'order.channel.timezone',
				'order.orderStatus',
				'order.orderType',
				'order.shippingCoreCountry',
				'order.shippingCoreCountryState',
			],
		});
		this.resume();
		if (this.openPrintModal) {
			this.openPrintModal = false;
			setTimeout(function() {
				const frame = window.frames['pdf-print'];
				frame.print();
			}, 250);
		}
		this.calculateItemPrice();
		this.order = this.fulfillmentOrder.order;
	};

	@action.bound
	getWarehouseCodesForDisplay(item) {
		let code = _.find(item.warehouseOrganization.convertWarehouses, convertWarehouse => {
			return (
				convertWarehouse.channelId == item.order.channelId && convertWarehouse.warehouseId == item.assignedWarehouseId
			);
		});

		if (isSome(code)) {
			return code.name;
		} else {
			return '-';
		}
	}

	@action.bound
	getFulfillmentOrder = async (id, options) => {
		await this.fulfillmentOrderStore.getFulfillmentOrder(id, options);
		this.fulfillmentOrder = this.fulfillmentOrderStore.item;
		this.fulfillmentOrderItems = this.fulfillmentOrder.fulfillmentOrderItems;
		this.selectedItem = this.fulfillmentOrder;
		//If there is any address data on FO show those data
		this.orderAddressData = this.fulfillmentOrder;
		this.hasLabel = _.find(this.fulfillmentOrder.shipmentInfos, item => {
			return (
				(isSome(item.label) && item.shipmentInfoStatus.abrv == 'active') ||
				((this.fulfillmentOrder.fulfillmentOrderStatus.abrv == fulfillmentOrderStatus.shippedExternally ||
					this.fulfillmentOrder.fulfillmentOrderStatus.abrv == fulfillmentOrderStatus.exitScan) &&
					item.shipmentInfoStatus.abrv == 'active')
			);
		});
		this.isBoxRequired =
			this.fulfillmentOrder.fulfillmentOrderItems.length > 1 ||
			(this.fulfillmentOrder.fulfillmentOrderItems[0] && this.fulfillmentOrder.fulfillmentOrderItems[0].quantity > 1);
		this.isVendorOrder = this.fulfillmentOrder.order.channel.channelType.name === channelType.amazonVendor;

		this.editFulfillmentOrderSplit =
			(this.fulfillmentOrder.isSplit ||
				(this.fulfillmentOrder.originalFulfillmentOrder != null &&
					this.fulfillmentOrder.originalFulfillmentOrder.isSplit)) &&
			!this.hasLabel;

		this.isPrintingPackingSlipAllowed =
			this.fulfillmentOrder.seller.sellerOrganizationInfo.printPackingSlip &&
			_.findIndex(onPrintPackingSlipStatuses, item => item == this.fulfillmentOrder.fulfillmentOrderStatus.abrv) != -1;

		const isPrintingAllowed =
			_.findIndex(onPrintLabelStatuses, item => item == this.fulfillmentOrder.fulfillmentOrderStatus.abrv) != -1;
		if (
			// Used for rendering Tracking Number and Is Buy Shipping flag in the Header. Reprint is not available.
			this.fulfillmentOrder.fulfillmentOrderStatus.abrv == fulfillmentOrderStatus.replaced ||
			this.fulfillmentOrder.fulfillmentOrderStatus.abrv == fulfillmentOrderStatus.reassigned
		) {
			this.activeShipmentInfo = _.find(this.fulfillmentOrder.shipmentInfos, item => {
				return item.shipmentInfoStatus.abrv == 'active';
			});
		}
		if (isPrintingAllowed) {
			this.activeShipmentInfo = _.find(this.fulfillmentOrder.shipmentInfos, item => {
				return item.shipmentInfoStatus.abrv == 'active';
			});
			if (this.activeShipmentInfo != null && this.activeShipmentInfo.hasOwnProperty('label')) {
				if (this.activeShipmentInfo.label != null) {
					this.pdfURL = createFileUrl(this.activeShipmentInfo.label);
				}
			}
		} else {
			this.pdfURL = null;
		}
		this.getLocale();
		if (
			this.fulfillmentOrder.onHoldReason != null &&
			this.fulfillmentOrder.onHoldReason.abrv == 'to%20be%20cancelled'
		) {
			this.canCancel = true;
		} else {
			this.canCancel = false;
		}
		this.productionModeCheck = this.fulfillmentOrder.productionMode;
		this.hasActiveShipment = this.activeShipmentInfo != null;
		this.fulfillmentOrderStatus = this.selectedItem.fulfillmentOrderStatus.abrv;
		this.isBuyShipping = this.activeShipmentInfo != null && this.activeShipmentInfo.isBuyShipping;
		this.isReentered =
			this.fulfillmentOrder.newShipByDate > this.fulfillmentOrder.shipByDate ||
			this.fulfillmentOrder.newShipByDate == this.fulfillmentOrder.shipByDate;
	};

	@action.bound
	printLabel = async () => {
		this.openPrintModal = true;
		if (this.fulfillmentOrder.fulfillmentOrderStatus.abrv !== fulfillmentOrderStatus.exitScan) {
			await this.shipmentInfoStore.reprintLabel(this.activeShipmentInfo.id, false);
			await this.initialize();
		} else {
			this.openPrintModal = false;
			setTimeout(function() {
				const frame = window.frames['pdf-print'];
				frame.print();
			}, 250);
		}
	};

	@action.bound
	printPackingSlip = async () => {
		this.suspend();
		try {
			await this.packingSlipStore.generatePdf(this.fulfillmentOrder.id);
			const pdfResponse = this.packingSlipStore.response;
			if (!pdfResponse.printedWithPrintNode) {
				const fileURL = createFileUrl(pdfResponse.packingSlip);
				printJS(fileURL);
			}
			this.rootStore.notificationStore.success('FULFILLMENTORDER.PACKINGSLIP.SUCCESS');
		} catch (err) {
			this.rootStore.notificationStore.error(['FULFILLMENTORDER.PACKINGSLIP.ERROR']);
		} finally {
			this.resume();
		}
	};

	@action.bound
	calculateItemPrice() {
		let itemPrice = 0;
		_.each(this.fulfillmentOrder.order.orderLines, orderLine => {
			itemPrice += orderLine.unitPrice * orderLine.quantity;
		});
		this.itemPrice = itemPrice;
	}

	@action.bound
	onProductionModeCheck = event => {
		this.productionModeCheck = event.currentTarget.checked;
		if (event.currentTarget.checked) {
			this.productionModeConfirmModal.open({
				message: ['ORDERVIEW.TOGGLEPRODUCTIONMODEON', { id: this.fulfillmentOrder.fulfillmentOrderId }],
			});
		} else {
			this.productionModeConfirmModal.open({
				message: ['ORDERVIEW.TOGGLEPRODUCTIONMODEOFF', { id: this.fulfillmentOrder.fulfillmentOrderId }],
			});
		}
	};

	@action.bound
	openVoidLabelConfirmModal = async () => {
		if (!this.plainLabelVoiding && this.isBuyShipping) {
			this.voidLabelConfirmModal.open({
				message: ['ORDERVIEW.VOIDLABELONCHANNEL', { fulfillmentOrderId: this.selectedItem.fulfillmentOrderId }],
			});
		} else if (this.plainLabelVoiding && this.isBuyShipping) {
			this.voidLabelModal.open();
		} else if (this.plainLabelVoiding && !this.isBuyShipping) {
			this.voidLabelConfirmModal.open({
				message: ['ORDERVIEW.VOIDLABEL', { fulfillmentOrderId: this.fulfillmentOrder.fulfillmentOrderId }],
			});
		} else if (this.reoptimizeLabelVoiding) {
			await this.reoptimizeOrder(false);
			this.reoptimizeLabelVoiding = false;
		}
	};

	@action.bound
	openVoidLabelModal() {
		this.plainLabelVoiding = true;
		this.openVoidLabelConfirmModal();
	}

	@action.bound
	openEditOrderModal = () => {
		this.editOrderModal.open();
	};

	@action.bound
	openReoptimizeConfirmModal = () => {
		this.reoptimizeConfirmModal.open({
			message: ['ORDERVIEW.REOPTIMIZEORDERCONFIRM', { id: this.selectedItem.fulfillmentOrderId }],
		});
	};

	@action.bound
	resetPrintingData = async () => {
		this.activeShipmentInfo = null;
		this.pdfURL = null;
	};

	@action.bound
	cancelFulfillmentOrder = async () => {
		this.suspend();
		try {
			await this.fulfillmentOrderStore.cancelFulfillmentOrder(
				this.fulfillmentOrder,
				sourceOfChange.cancelFulfillmentOrderList
			);
			this.rootStore.notificationStore.success([
				'ORDERDETAILVIEW.FULFILLMENTORDERCANCELED',
				{ id: this.fulfillmentOrder.fulfillmentOrderId },
			]);
			this.initialize();
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
			this.resume();
		}
	};

	@action.bound
	getLocale = () => {
		const locale = window.navigator.userLanguage || window.navigator.language;
		moment.locale(locale);
		const localeData = moment.localeData();
		// Required to be state due to the error log template which uses the store format and timezone
		this.format = localeData.longDateFormat('L');
		this.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
	};

	@action.bound
	pullLabel = async item => {
		this.suspend();
		try {
			const response = await new FulfillmentOrderProcessingStore(this.rootStore).createFulfillmentShipment(
				item.id,
				sourceOfChange.pullLabelOrderDetails,
				false
			);
			if (response.shipmentInfo) {
				this.rootStore.notificationStore.success(['ORDERVIEW.PULLLABELSUCCESS', { id: item.fulfillmentOrderId }]);
			} else if (response.message || response.errors) {
				// Error notification after shipment PII data is validated as false on the backend, before create shipment request.
				this.rootStore.notificationStore.error([
					response.message ? response.message : 'ORDERVIEW.PULLLABELERROR',
					{ id: item.fulfillmentOrderId, notification: response.errors ? response.errors.join(' ') : null },
				]);
			} else if (response.shipmentData.notifications) {
				// Error notification after shipment data is validated as true, but error occured on create shipment request.
				this.rootStore.notificationStore.error([
					(response.message = 'ORDERVIEW.PULLLABELERROR'),
					{ id: item.fulfillmentOrderId, notification: response.shipmentData.notifications.join(' ') },
				]);
			} else {
				// Generic error notification if no specific error message has been caught.
				this.rootStore.notificationStore.error(['ORDERVIEW.PULLLABELERROR', { id: item.fulfillmentOrderId }]);
			}
		} catch (err) {
			this.rootStore.notificationStore.error([
				err.data.message ? err.data.message : 'ORDERVIEW.PULLLABELERROR',
				{ id: item.fulfillmentOrderId },
			]);
		}
		await this.initialize();
		this.resume();
	};

	@action.bound
	updateProductionMode = async () => {
		this.suspend();
		try {
			await this.fulfillmentOrderStore.toggleProductionMode(
				this.fulfillmentOrder.id,
				!this.fulfillmentOrder.productionMode,
				sourceOfChange.productionModeOrderDetail
			);
			if (!this.fulfillmentOrder.productionMode) {
				this.rootStore.notificationStore.success([
					'ORDERVIEW.TOGGLEPRODUCTIONMODESUCCESSON',
					{ id: this.fulfillmentOrder.fulfillmentOrderId },
				]);
			} else {
				this.rootStore.notificationStore.success([
					'ORDERVIEW.TOGGLEPRODUCTIONMODESUCCESSOFF',
					{ id: this.fulfillmentOrder.fulfillmentOrderId },
				]);
			}
			await this.initialize();
		} catch (err) {
			if (err.data != null && err.data.details != null) {
				this.rootStore.notificationStore.error(err.data.details);
			} else {
				notificationError({ store: this.rootStore.notificationStore, err });
			}
			this.productionModeCheck = !this.productionModeCheck;
		}
		this.resume();
	};

	@action.bound
	reoptimizeOrder = async (voidOnChannel = false) => {
		this.suspend;
		try {
			await this.fulfillmentOrderStore.reoptimize(
				{
					fulfillmentOrderIds: [this.selectedItem.id],
					voidOnChannel: voidOnChannel,
				},
				sourceOfChange.reoptimizeOrderDetail
			);
			this.rootStore.notificationStore.success([
				'ORDERVIEW.REOPTIMIZEORDERSUCCESS',
				{ id: this.selectedItem.fulfillmentOrderId },
			]);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		await this.initialize();
		this.resume();
	};

	@action.bound
	displayPopoverButton = width => {
		//1165px is equal to 1440 width resolution
		if (width > 1500) {
			this.shouldDisplayActionButton = false;
		} else {
			this.shouldDisplayActionButton = true;
		}
	};

	errorLogModal = new ModalParams();
	historyLogModal = new ModalParams();
	assignmentModal = new ModalParams();

	onHoldModal = new ModalParams({
		onClose: async () => {
			if (this.reload) {
				this.reload = false;
				await this.initialize();
			}
		},
	});

	changeShippingModal = new ModalParams({
		onClose: async () => {
			if (this.reload) {
				this.reload = false;
				await this.initialize();
			}
		},
	});

	editOrderModal = new ModalParams({
		onClose: async () => {
			if (this.reload) {
				this.reload = false;
				await this.initialize();
			}
		},
	});

	productionModeConfirmModal = new ConfirmModalParams({
		onConfirm: async () => {
			await this.updateProductionMode();
		},
		onClose: () => {
			this.productionModeCheck = this.fulfillmentOrder.productionMode;
			this.productionModeConfirmModal.close();
		},
	});

	cancelConfirmModal = new ConfirmModalParams({
		onConfirm: async () => {
			await this.cancelFulfillmentOrder(this.fulfillmentOrder);
		},
	});

	statusChangeModal = new ModalParams({
		onClose: async () => {
			if (this.reload) {
				this.reload = false;
				await this.initialize();
			}
		},
	});

	voidLabelModal = new ModalParams({
		onClose: async () => {
			if (this.reload) {
				this.reload = false;
				await this.initialize();
			}
		},
	});

	neverShippedModal = new ModalParams({
		onClose: async () => {
			if (this.reload) {
				this.reload = false;
				await this.initialize();
			}
		},
	});

	splitFulfillmentOrderModal = new ModalParams({
		onClose: async reload => {
			if (reload) {
				await this.initialize();
			}
		},
	});

	reoptimizeConfirmModal = new ConfirmModalParams({
		onConfirm: async () => {
			this.suspend();
			this.plainLabelVoiding = false;
			this.reoptimizeLabelVoiding = true;
			await this.openVoidLabelConfirmModal();
			this.reoptimizeConfirmModal.close();
			this.resume();
		},
		onCancel: async () => {
			this.reoptimizeConfirmModal.close();
		},
	});

	@action.bound
	openNeverShippedConfirmModal() {
		this.showVoidOnChannel = this.isBuyShipping;
		this.neverShippedModal.open();
	}

	voidLabelConfirmModal = new ConfirmModalParams({
		onConfirm: async () => {
			this.suspend();
			try {
				if (this.reoptimizeLabelVoiding) {
					await this.reoptimizeOrder(true);
					this.reoptimizeLabelVoiding = false;
				} else {
					this.plainLabelVoiding = true;
					let voidLabelOption = this.activeShipmentInfo.isBuyShipping ? 1 : 0;
					const response = await this.shipmentInfoStore.voidLabel(
						{ fulfillmentOrderId: this.fulfillmentOrder.id },
						voidLabelOption,
						sourceOfChange.voidActiveLabelConfirmModal
					);

					handleVoidLabelResponseNotification(this.rootStore, response, this.fulfillmentOrder.fulfillmentOrderId);

					this.pdfURL = null;
					this.voidLabelConfirmModal.close();
					this.initialize();
				}
			} catch (err) {
				notificationError({ store: this.rootStore.notificationStore, err });
			}
			this.resume();
		},
		onCancel: async () => {
			if (this.reoptimizeLabelVoiding) {
				await this.reoptimizeOrder(false);
				this.reoptimizeLabelVoiding = false;
			} else {
				this.plainLabelVoiding = true;
			}
			this.voidLabelConfirmModal.close();
		},
	});

	pullLabelProductionModeConfirmModal = new ConfirmModalParams({
		onConfirm: () => {
			this.pullLabel(this.fulfillmentOrder);
		},
	});

	pullLabelConfirmModal = new ConfirmModalParams({
		onConfirm: () => {
			if (
				(process.env.NODE_ENV === 'staging' ||
					process.env.NODE_ENV == 'test' ||
					process.env.NODE_ENV == 'development') &&
				this.fulfillmentOrder.productionMode
			) {
				this.pullLabelProductionModeConfirmModal.open({ message: 'PRODUCTIONPULLLABEL' });
			} else {
				this.pullLabel(this.fulfillmentOrder);
			}
		},
	});

	markAsShippedModal = new ModalParams({
		onClose: async () => {
			if (this.reload) {
				this.reload = false;
				await this.initialize();
			}
		},
	});
}

export default OrderDetailViewStore;



// WEBPACK FOOTER //
// ./src/modules/order/stores/OrderDetailViewStore.js